package com.arena_web.ui.sections.header

import androidx.compose.runtime.*
import com.arena_web.core.viewmodel.LoginViewModel
import com.arena_web.data.dto.ClubDto
import com.arena_web.data.dto.UpdateActiveRoleRequest
import com.arena_web.data.websocket.UserWsEvent
import com.arena_web.di.LocalAppContainer
import com.arena_web.navigation.Routes
import com.arena_web.ui.components.icons.Icon
import com.arena_web.ui.components.navigation.BusinessSidebarNav
import com.arena_web.ui.components.navigation.BusinessSidebarTab
import com.arena_web.ui.components.navigation.HeaderNavButton
import com.arena_web.ui.localization.LocalAppStrings
import com.arena_web.ui.theme.*
import com.arena_web.ui.utils.*
import com.arena_web.utils.OAuthHelper
import com.arena_web.utils.Storage
import com.arena_web.utils.StorageKeys
import com.varabyte.kobweb.compose.css.*
import com.varabyte.kobweb.compose.foundation.layout.*
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.core.rememberPageContext
import com.varabyte.kobweb.silk.components.text.SpanText
import kotlinx.coroutines.launch
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Img

@Composable
fun BusinessHeader(
    modifier: Modifier = Modifier,
    activeTab: BusinessSidebarTab? = null,
    clubs: List<ClubDto> = emptyList(),
    selectedClubId: String? = null,
    onClubSelected: (String) -> Unit = {},
    onAddClub: () -> Unit = {}
) {
    val ctx = rememberPageContext()
    val container = LocalAppContainer.current
    val t = LocalAppStrings.current
    val scope = rememberCoroutineScope()

    var isLoggedIn by remember { mutableStateOf(Storage.contains(StorageKeys.ACCESS_TOKEN)) }
    var userName by remember {
        mutableStateOf(
            Storage.get(StorageKeys.FULL_NAME)
                ?: Storage.get(StorageKeys.USERNAME)
                ?: ""
        )
    }
    var avatarUrl by remember { mutableStateOf(Storage.get(StorageKeys.AVATAR_URL)) }

    // Notifications — cached in AppContainer
    val notificationCount by container.unreadNotificationCount.collectAsState()
    var activeDropdown by remember { mutableStateOf<String?>(null) }

    LaunchedEffect(isLoggedIn) {
        if (!isLoggedIn) return@LaunchedEffect
        container.ensureNotificationsCountLoaded()
    }

    // Real-time notification updates
    LaunchedEffect(isLoggedIn) {
        if (!isLoggedIn) return@LaunchedEffect
        container.userWsClient.events.collect { event ->
            if (event is UserWsEvent.NewNotification) {
                container.refreshNotifications()
            }
        }
    }

    Box(
        modifier = modifier.fillMaxWidth().sHeight(63)
    ) {
        // Logo - left
        Row(
            modifier = Modifier
                .align(Alignment.CenterStart)
                .sGap(22)
                .sPaddingLeft(8)
                .cursor(Cursor.Pointer)
                .onClick { ctx.router.navigateTo(Routes.DASHBOARD) },
            verticalAlignment = Alignment.CenterVertically
        ) {
            com.arena_web.ui.sections.header.components.BrandLogo()
        }

        // Center - BusinessSidebarNav
        Row(modifier = Modifier.align(Alignment.Center)) {
            BusinessSidebarNav(
                activeTab = activeTab,
                onTabSelected = { tab ->
                    ctx.router.navigateTo(tab.route)
                }
            )
        }

        // Right - Actions
        Row(
            modifier = Modifier
                .align(Alignment.CenterEnd)
                .sGap(4),
            verticalAlignment = Alignment.CenterVertically
        ) {
            if (isLoggedIn) {
                // Club switcher
                if (clubs.isNotEmpty()) {
                    BusinessClubSwitcher(
                        clubs = clubs,
                        selectedClubId = selectedClubId,
                        expanded = activeDropdown == "clubs",
                        onToggle = { activeDropdown = if (activeDropdown == "clubs") null else "clubs" },
                        onClubSelected = { id ->
                            onClubSelected(id)
                            activeDropdown = null
                        },
                        onAddClub = {
                            activeDropdown = null
                            onAddClub()
                        }
                    )
                }

                // Notification button (icon only)
                BusinessNotificationButton(
                    count = notificationCount,
                    expanded = activeDropdown == "notifications",
                    onToggle = { activeDropdown = if (activeDropdown == "notifications") null else "notifications" }
                )

                // Profile pill
                BusinessProfilePill(
                    name = userName.ifEmpty { t.headerUserFallbackName },
                    avatarUrl = avatarUrl,
                    expanded = activeDropdown == "profile",
                    onToggle = { activeDropdown = if (activeDropdown == "profile") null else "profile" },
                    onLoggedOut = {
                        isLoggedIn = false
                        userName = ""
                        avatarUrl = null
                        container.updateNotificationCount(0)
                    }
                )
            } else {
                val onLoggedIn: () -> Unit = {
                    userName = Storage.get(StorageKeys.FULL_NAME)
                        ?: Storage.get(StorageKeys.USERNAME)
                        ?: ""
                    avatarUrl = Storage.get(StorageKeys.AVATAR_URL)
                    isLoggedIn = true
                }
                BusinessHeaderGoogleButton(onLoggedIn = onLoggedIn)
                BusinessHeaderAppleButton(onLoggedIn = onLoggedIn)
            }
        }
    }
}

// =============================================================================
// Business notification button (simplified — icon + badge, no dropdown content)
// =============================================================================

@Composable
private fun BusinessNotificationButton(
    count: Int,
    expanded: Boolean,
    onToggle: () -> Unit
) {
    Box(modifier = Modifier.position(Position.Relative)) {
        HeaderNavButton(onClick = { onToggle() }) {
            Icon(src = AppIcons.Notifications)

            if (count > 0) {
                Box(
                    modifier = Modifier
                        .sSize(12)
                        .borderRadius(50.percent)
                        .backgroundColor(AppColors.Primary)
                        .position(Position.Absolute)
                        .sTop(0)
                        .sRight(2),
                    contentAlignment = Alignment.Center
                ) {
                    SpanText(
                        count.toString(),
                        modifier = Modifier
                            .sFontSize(8)
                            .color(AppColors.PrimaryWhite)
                            .styleModifier { property("transform", "translateY(0.5px)") }
                    )
                }
            }
        }
    }
}

// =============================================================================
// Business profile pill — "Switch to Player" + Logout
// =============================================================================

@Composable
private fun BusinessProfilePill(
    name: String,
    avatarUrl: String?,
    expanded: Boolean,
    onToggle: () -> Unit,
    onLoggedOut: () -> Unit
) {
    val ctx = rememberPageContext()
    val container = LocalAppContainer.current
    val t = LocalAppStrings.current
    var isLoggingOut by remember { mutableStateOf(false) }
    val scope = rememberCoroutineScope()

    Box(modifier = Modifier.position(Position.Relative)) {
        Row(
            modifier = Modifier
                .sBorderRadius(26)
                .sPaddingLeft(4)
                .sPaddingRight(16)
                .sPaddingTop(4)
                .sPaddingBottom(4)
                .sGap(24)
                .cursor(Cursor.Pointer)
                .styleModifier {
                    property("border", "0.3px solid transparent")
                    property("background",
                        "${AppGradients.InteractiveSecondary} padding-box, " +
                        "linear-gradient(0deg, var(--arena-bg), var(--arena-bg)) padding-box, " +
                        "${AppGradients.ButtonBorderGradientPrimary} border-box")
                }
                .onClick { onToggle() },
            verticalAlignment = Alignment.CenterVertically
        ) {
            Box(
                modifier = Modifier
                    .sSize(44)
                    .sBorderRadius(100)
                    .overflow(Overflow.Hidden)
                    .backgroundColor(AppColors.Text.Primary),
                contentAlignment = Alignment.Center
            ) {
                if (avatarUrl != null) {
                    Div(attrs = {
                        ref { el ->
                            el.innerHTML = "<img src=\"$avatarUrl\" referrerpolicy=\"no-referrer\" " +
                                "onerror=\"this.style.display='none'\" " +
                                "style=\"width:100%;height:100%;object-fit:cover;\" />"
                            onDispose {}
                        }
                        style { property("width", "100%"); property("height", "100%") }
                    })
                } else {
                    Icon(src = AppIcons.Profile)
                }
            }
            SpanText(
                name,
                modifier = Modifier.bodyLRegular().color(com.arena_web.ui.theme.ThemeVars.Text2)
            )
            Icon(src = AppIcons.ChevronDown)
        }

        // Dropdown
        if (expanded) {
            Box(
                modifier = Modifier
                    .position(Position.Absolute)
                    .sTop(56)
                    .sRight(0)
                    .sBorderRadius(16)
                    .sPadding(8)
                    .zIndex(100)
                    .styleModifier {
                        property("border", "0.3px solid transparent")
                        property("background",
                            "linear-gradient(6deg, rgba(0, 59, 174, 0.12) 26.9%, rgba(3, 87, 230, 0.12) 67.5%) padding-box, " +
                            "linear-gradient(0deg, var(--l-s2, #0A0D14), var(--l-s2, #0A0D14)) padding-box, " +
                            "${AppGradients.ButtonBorderGradientPrimary} border-box")
                        property("min-width", "calc(160 * var(--scale, 1))")
                    }
            ) {
                Column(modifier = Modifier.fillMaxWidth().sGap(2)) {
                    // Switch to Player
                    Row(
                        modifier = Modifier
                            .fillMaxWidth()
                            .sBorderRadius(10)
                            .sPadding(12, 16)
                            .sGap(10)
                            .cursor(Cursor.Pointer)
                            .styleModifier {
                                property("transition", "background 0.15s ease")
                            }
                            .onClick {
                                scope.launch {
                                    container.userRepository?.updateActiveRole(
                                        UpdateActiveRoleRequest(role = "PLAYER")
                                    )?.onSuccess {
                                        Storage.save(StorageKeys.ACTIVE_ROLE, "PLAYER")
                                        ctx.router.navigateTo(Routes.ARENA)
                                    }
                                    onToggle()
                                }
                            },
                        verticalAlignment = Alignment.CenterVertically
                    ) {
                        SpanText(
                            t.headerSwitchToPlayer,
                            modifier = Modifier.bodyLMedium().color(com.arena_web.ui.theme.ThemeVars.Text2)
                        )
                    }

                    // Logout
                    Row(
                        modifier = Modifier
                            .fillMaxWidth()
                            .sBorderRadius(10)
                            .sPadding(12, 16)
                            .sGap(10)
                            .cursor(if (isLoggingOut) Cursor.NotAllowed else Cursor.Pointer)
                            .styleModifier {
                                property("transition", "background 0.15s ease")
                            }
                            .onClick {
                                if (!isLoggingOut) {
                                    isLoggingOut = true
                                    scope.launch {
                                        val logoutUseCase = container.getLogoutUseCase()
                                        logoutUseCase()
                                        container.clearAuthenticatedClients()
                                        OAuthHelper.resetGoogleSession()
                                        onToggle()
                                        onLoggedOut()
                                    }
                                }
                            },
                        verticalAlignment = Alignment.CenterVertically
                    ) {
                        SpanText(
                            if (isLoggingOut) t.headerLoggingOut else t.headerLogout,
                            modifier = Modifier.bodyLMedium().color(com.arena_web.ui.theme.ThemeVars.Text2)
                        )
                    }
                }
            }
        }
    }
}

// =============================================================================
// Auth login buttons for business header
// =============================================================================

@Composable
private fun BusinessHeaderGoogleButton(onLoggedIn: () -> Unit) {
    val container = LocalAppContainer.current
    val t = LocalAppStrings.current
    val loginViewModel = remember {
        LoginViewModel(
            oauthLoginUseCase = container.getOAuthLoginUseCase(),
            container = container
        )
    }
    var isLoading by remember { mutableStateOf(false) }

    Row(
        modifier = Modifier
            .sHeight(48)
            .sBorderRadius(26)
            .sPadding(8, 18)
            .sGap(10)
            .cursor(if (isLoading) Cursor.NotAllowed else Cursor.Pointer)
            .styleModifier {
                property("border", "0.3px solid transparent")
                property(
                    "background",
                    "${AppGradients.InteractiveSecondary} padding-box, " +
                        "linear-gradient(0deg, var(--arena-bg), var(--arena-bg)) padding-box, " +
                        "${AppGradients.ButtonBorderGradientPrimary} border-box"
                )
            }
            .onClick {
                if (!isLoading) {
                    isLoading = true
                    OAuthHelper.triggerGoogleSignIn(
                        onSuccess = { googleUser ->
                            val lang = OAuthHelper.localeToLanguageName(googleUser.locale)
                            loginViewModel.loginWithOAuth(
                                provider = "google",
                                providerId = googleUser.sub,
                                email = googleUser.email,
                                fullName = googleUser.name,
                                avatarUrl = googleUser.picture,
                                idToken = googleUser.idToken,
                                languages = lang?.let { listOf(it) },
                                onSuccess = { onLoggedIn() }
                            )
                        },
                        onError = { error ->
                            console.error("Google sign-in error: $error")
                            isLoading = false
                        }
                    )
                }
            },
        verticalAlignment = Alignment.CenterVertically
    ) {
        Icon(src = AppIcons.Google, modifier = Modifier.sSize(20))
        SpanText(
            if (isLoading) t.headerSigningIn else "Google",
            modifier = Modifier.bodyLMedium().color(com.arena_web.ui.theme.ThemeVars.Text2)
        )
    }
}

@Composable
private fun BusinessHeaderAppleButton(onLoggedIn: () -> Unit) {
    val container = LocalAppContainer.current
    val t = LocalAppStrings.current
    val loginViewModel = remember {
        LoginViewModel(
            oauthLoginUseCase = container.getOAuthLoginUseCase(),
            container = container
        )
    }
    var isLoading by remember { mutableStateOf(false) }

    Row(
        modifier = Modifier
            .sHeight(48)
            .sBorderRadius(26)
            .sPadding(8, 18)
            .sGap(10)
            .cursor(if (isLoading) Cursor.NotAllowed else Cursor.Pointer)
            .styleModifier {
                property("border", "0.3px solid transparent")
                property(
                    "background",
                    "${AppGradients.InteractiveSecondary} padding-box, " +
                        "linear-gradient(0deg, var(--arena-bg), var(--arena-bg)) padding-box, " +
                        "${AppGradients.ButtonBorderGradientPrimary} border-box"
                )
            }
            .onClick {
                if (!isLoading) {
                    isLoading = true
                    OAuthHelper.signInWithApple(
                        onSuccess = { email, name, providerId, idToken ->
                            loginViewModel.loginWithOAuth(
                                provider = "apple",
                                providerId = providerId,
                                email = email,
                                fullName = name,
                                idToken = idToken,
                                onSuccess = { onLoggedIn() }
                            )
                        },
                        onError = { error ->
                            console.error("Apple sign-in error: $error")
                            isLoading = false
                        }
                    )
                }
            },
        verticalAlignment = Alignment.CenterVertically
    ) {
        Icon(src = AppIcons.Apple, modifier = Modifier.sSize(18))
        SpanText(
            if (isLoading) t.headerSigningIn else "Apple",
            modifier = Modifier.bodyLMedium().color(com.arena_web.ui.theme.ThemeVars.Text2)
        )
    }
}

// =============================================================================
// Club switcher — pill with dropdown
// =============================================================================

@Composable
private fun BusinessClubSwitcher(
    clubs: List<ClubDto>,
    selectedClubId: String?,
    expanded: Boolean,
    onToggle: () -> Unit,
    onClubSelected: (String) -> Unit,
    onAddClub: () -> Unit = {}
) {
    val t = LocalAppStrings.current
    val selectedClub = clubs.find { it.id == selectedClubId }

    Box(modifier = Modifier.position(Position.Relative)) {
        Row(
            modifier = Modifier
                .sBorderRadius(22)
                .sHeight(44)
                .sPadding(0, 16)
                .sGap(8)
                .cursor(Cursor.Pointer)
                .styleModifier {
                    property("border", "0.3px solid transparent")
                    property("background",
                        "${AppGradients.InteractiveSecondary} padding-box, " +
                        "linear-gradient(0deg, var(--arena-bg), var(--arena-bg)) padding-box, " +
                        "${AppGradients.ButtonBorderGradientPrimary} border-box")
                }
                .onClick { onToggle() },
            verticalAlignment = Alignment.CenterVertically
        ) {
            SpanText(
                selectedClub?.name ?: t.headerSelectClub,
                modifier = Modifier
                    .bodyLMedium()
                    .color(com.arena_web.ui.theme.ThemeVars.Text2)
                    .styleModifier {
                        property("white-space", "nowrap")
                        property("max-width", s(160))
                        property("overflow", "hidden")
                        property("text-overflow", "ellipsis")
                    }
            )
            Icon(
                src = AppIcons.ChevronDown,
                modifier = Modifier.sSize(16).styleModifier {
                    if (expanded) property("transform", "rotate(180deg)")
                }
            )
        }

        if (expanded) {
            Box(
                modifier = Modifier
                    .position(Position.Absolute)
                    .sTop(52)
                    .sRight(0)
                    .sBorderRadius(16)
                    .sPadding(8)
                    .zIndex(100)
                    .styleModifier {
                        property("border", "0.3px solid transparent")
                        property("background",
                            "linear-gradient(6deg, rgba(0, 59, 174, 0.12) 26.9%, rgba(3, 87, 230, 0.12) 67.5%) padding-box, " +
                            "linear-gradient(0deg, var(--l-s2, #0A0D14), var(--l-s2, #0A0D14)) padding-box, " +
                            "${AppGradients.ButtonBorderGradientPrimary} border-box")
                        property("min-width", s(200))
                    }
            ) {
                Column(modifier = Modifier.fillMaxWidth().sGap(2)) {
                    clubs.forEach { club ->
                        val isSelected = club.id == selectedClubId
                        Row(
                            modifier = Modifier
                                .fillMaxWidth()
                                .sBorderRadius(10)
                                .sPadding(12, 16)
                                .sGap(10)
                                .cursor(Cursor.Pointer)
                                .then(
                                    if (isSelected) Modifier.styleModifier {
                                        property("background", "rgba(0, 97, 255, 0.15)")
                                    } else Modifier
                                )
                                .onClick { onClubSelected(club.id) },
                            verticalAlignment = Alignment.CenterVertically
                        ) {
                            SpanText(
                                club.name,
                                modifier = Modifier
                                    .bodyLMedium()
                                    .color(if (isSelected) org.jetbrains.compose.web.css.Color("var(--l-text-strong, #FFFFFF)") else com.arena_web.ui.theme.ThemeVars.Text2)
                                    .styleModifier {
                                        property("white-space", "nowrap")
                                    }
                            )
                        }
                    }

                    // Divider
                    Box(
                        modifier = Modifier
                            .fillMaxWidth()
                            .sHeight(1)
                            .styleModifier { property("background", "var(--arena-glass-strong)") }
                    )

                    // Add Club
                    Row(
                        modifier = Modifier
                            .fillMaxWidth()
                            .sBorderRadius(10)
                            .sPadding(12, 16)
                            .sGap(10)
                            .cursor(Cursor.Pointer)
                            .onClick { onAddClub() },
                        verticalAlignment = Alignment.CenterVertically
                    ) {
                        Icon(src = AppIcons.Add, modifier = Modifier.sSize(16))
                        SpanText(
                            t.headerAddClub,
                            modifier = Modifier.bodyLMedium().color(com.arena_web.ui.theme.ThemeVars.Text2)
                        )
                    }
                }
            }
        }
    }
}
